import { useCallback, useEffect, useMemo, useState } from 'react';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button } from '@ui-component-library';

import SellTimberMap from './components/SellTimberMap';
import { coercePlacedMarkersArray, hasMapGeometrySelection, newSellTimberMarkerId } from './helpers/api';
import {
  SELL_TIMBER_MARKER_ICON_OPTIONS,
  sellTimberTimberMarkerIconEntries
} from './helpers/sellTimberMarkerIcons';
import { clipDrawnPolygonToPropertyBorder } from './helpers/sellTimberClipDrawnPolygon';
import { loadSellTimberMapPropertyBundle } from './helpers/sellTimberMapPropertyLoad';
import { buildStep2InitialState, MAP_MODES } from './helpers/sellTimberMapStep2State';

function toggleIdInList(list, id) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/** Instruction line under map mode buttons (remove mode takes precedence). */
function getSellTimberStep2MapStatus({ removeMode, mapMode, fellingAppsLoading, fellingAppsError }) {
  if (removeMode) {
    return {
      className: 'text-amber-900',
      content:
        'Borttagningsläge: endast dina val visas. Klicka på det du vill ta bort i kartan. Klicka på Klar när du är färdig.'
    };
  }
  switch (mapMode) {
    case MAP_MODES.sections:
      return {
        className: 'text-charcoal-600',
        content: 'Klicka på en eller flera avdelningar för att välja dem.'
      };
    case MAP_MODES.fellings:
      return {
        className: 'text-charcoal-600',
        content: fellingAppsLoading
          ? 'Laddar avverkningsanmälningar från Skogsstyrelsen…'
          : fellingAppsError || 'Klicka på en eller flera avverkningsanmälningar för att välja dem.'
      };
    case MAP_MODES.draw:
      return {
        className: 'text-charcoal-600',
        content: 'Rita ett eller flera områden.'
      };
    case MAP_MODES.marker:
      return {
        className: 'text-charcoal-600',
        content:
          'Klicka i kartan för att placera markören.'
      };
    default:
      return null;
  }
}

export default function SellTimberStep2({ wizard, dispatch }) {
  const selectedProperty = wizard.adCreateData?.selectedProperty ?? null;
  const initialMapState = wizard.geometry;

  const [mapState, setMapState] = useState(() =>
    buildStep2InitialState(initialMapState, wizard.mapPropertyCache, selectedProperty?.ref)
  );

  const geometryPayload = useMemo(
    () => ({
      mapMode: mapState.mapMode,
      sections: mapState.sections,
      borderCoords: mapState.borderCoords,
      selectedSectionLocalIds: mapState.selectedSectionLocalIds,
      selectedFellingApplicationIds: mapState.selectedFellingApplicationIds,
      fellingApplicationsGeojson: mapState.fellingApplicationsGeojson,
      drawnPolygons: mapState.drawnPolygons,
      placedMarkers: mapState.placedMarkers,
      customAreaFromAd: mapState.customAreaFromAd
    }),
    [
      mapState.mapMode,
      mapState.sections,
      mapState.borderCoords,
      mapState.selectedSectionLocalIds,
      mapState.selectedFellingApplicationIds,
      mapState.fellingApplicationsGeojson,
      mapState.drawnPolygons,
      mapState.placedMarkers,
      mapState.customAreaFromAd
    ]
  );

  const hasMapSelection = hasMapGeometrySelection(geometryPayload);

  const loading = mapState.propertyFetch.status === 'loading';
  const loadError = mapState.propertyFetch.error;
  const fellingAppsLoading = mapState.fellingFetch.status === 'loading';
  const fellingAppsError = mapState.fellingFetch.error;

  const mapModeStatus = getSellTimberStep2MapStatus({
    removeMode: mapState.removeMode,
    mapMode: mapState.mapMode,
    fellingAppsLoading,
    fellingAppsError
  });

  useEffect(() => {
    if (!hasMapSelection && mapState.removeMode) {
      setMapState((s) => ({ ...s, removeMode: false }));
    }
  }, [hasMapSelection, mapState.removeMode]);

  /** Apply wizard map cache when preload completes or when returning to this step. */
  useEffect(() => {
    const c = wizard.mapPropertyCache;
    const ref = selectedProperty?.ref;
    if (!ref || !c || String(c.ref) !== String(ref)) return;
    if (c.propertyFetch?.status !== 'success') return;

    setMapState((s) => ({
      ...s,
      sections: Array.isArray(c.sections) ? c.sections : [],
      borderCoords: c.borderCoords ?? null,
      propertyGeojson: c.propertyGeojson ?? null,
      fellingApplicationsGeojson: c.fellingApplicationsGeojson ?? null,
      propertyFetch: c.propertyFetch,
      fellingFetch: c.fellingFetch
    }));
  }, [wizard.mapPropertyCache, selectedProperty?.ref]);

  /**
   * Load map bootstrap only if not already in wizard cache (e.g. skipped step 1 preload).
   * Shares `loadSellTimberMapPropertyBundle` with step 1 — in-flight dedupe avoids double requests.
   */
  useEffect(() => {
    const ref = selectedProperty?.ref;
    if (!ref) {
      setMapState((s) => ({
        ...s,
        propertyFetch: { status: 'error', error: 'Ingen fastighet vald. Gå tillbaka och välj en fastighet.' }
      }));
      return undefined;
    }

    const c = wizard.mapPropertyCache;
    if (c && String(c.ref) === String(ref) && c.propertyFetch?.status === 'success') {
      return undefined;
    }
    /** Step 1 prefetch may have been aborted on unmount before dispatch — attach to the same in-flight promise. */
    if (c && String(c.ref) === String(ref) && c.propertyFetch?.status === 'loading') {
      void loadSellTimberMapPropertyBundle(ref).then((bundle) => {
        dispatch({ type: 'MAP_PROPERTY_CACHE_SET', payload: bundle });
      });
      return undefined;
    }

    dispatch({ type: 'MAP_PROPERTY_CACHE_LOADING', ref });
    void loadSellTimberMapPropertyBundle(ref).then((bundle) => {
      dispatch({ type: 'MAP_PROPERTY_CACHE_SET', payload: bundle });
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when entering step 2 for this property; cache read intentionally
  }, [selectedProperty?.ref, dispatch]);

  const handleModeChange = (mode) => {
    setMapState((s) => ({
      ...s,
      mapMode: mode,
      markerMoveTargetId: mode === MAP_MODES.marker ? s.markerMoveTargetId : null
    }));
  };

  const selectMarkerModeWithIcon = useCallback((iconKey) => {
    setMapState((s) => ({
      ...s,
      continueError: null,
      mapMode: MAP_MODES.marker,
      selectedMarkerIcon: iconKey,
      markerMoveTargetId: null
    }));
  }, []);

  const toggleSection = useCallback((localKey) => {
    setMapState((s) => ({
      ...s,
      continueError: null,
      selectedSectionLocalIds: toggleIdInList(s.selectedSectionLocalIds, localKey)
    }));
  }, []);

  const toggleFellingApplication = useCallback((applicationId) => {
    const key = String(applicationId);
    setMapState((s) => ({
      ...s,
      continueError: null,
      selectedFellingApplicationIds: toggleIdInList(s.selectedFellingApplicationIds, key)
    }));
  }, []);

  const onDrawComplete = useCallback((feature) => {
    if (!feature?.geometry) return;
    setMapState((s) => {
      const clipped = clipDrawnPolygonToPropertyBorder(
        feature,
        s.borderCoords,
        s.propertyGeojson?.geometry
      );
      if (clipped == null) {
        return {
          ...s,
          continueError:
            'Inget av det ritade området ligger inom fastighetsgränsen. Rita inom den vita fastighetslinjen.'
        };
      }
      return {
        ...s,
        continueError: null,
        drawnPolygons: [...s.drawnPolygons, clipped]
      };
    });
  }, []);

  const onMarkerAddAt = useCallback(
    (latlng) => {
      const icon = mapState.selectedMarkerIcon;
      const id = newSellTimberMarkerId();
      const feature = {
        type: 'Feature',
        properties: {
          sellTimberMarkerId: id,
          name: SELL_TIMBER_MARKER_ICON_OPTIONS[icon].defaultName,
          icon
        },
        geometry: { type: 'Point', coordinates: [latlng.lng, latlng.lat] }
      };
      setMapState((s) => ({
        ...s,
        continueError: null,
        placedMarkers: [...s.placedMarkers, feature]
      }));
    },
    [mapState.selectedMarkerIcon]
  );

  const onMarkerSelectForMove = useCallback((markerId) => {
    setMapState((s) => ({ ...s, continueError: null, markerMoveTargetId: String(markerId) }));
  }, []);

  const onMarkerMoveTo = useCallback((markerId, latlng) => {
    const id = String(markerId);
    setMapState((s) => ({
      ...s,
      continueError: null,
      markerMoveTargetId: null,
      placedMarkers: s.placedMarkers.map((f) => {
        const mid = f?.properties?.sellTimberMarkerId;
        if (String(mid) !== id) return f;
        return {
          ...f,
          geometry: { type: 'Point', coordinates: [latlng.lng, latlng.lat] }
        };
      })
    }));
  }, []);

  const onRemoveDrawnPolygon = useCallback((index) => {
    setMapState((s) => ({
      ...s,
      continueError: null,
      drawnPolygons: s.drawnPolygons.filter((_, i) => i !== index)
    }));
  }, []);

  const onRemoveMarker = useCallback((markerId) => {
    const id = String(markerId);
    setMapState((s) => ({
      ...s,
      continueError: null,
      markerMoveTargetId: s.markerMoveTargetId === id ? null : s.markerMoveTargetId,
      placedMarkers: s.placedMarkers.filter((f) => String(f?.properties?.sellTimberMarkerId) !== id)
    }));
  }, []);

  const onRemoveHydratedAd = useCallback(() => {
    setMapState((s) => ({ ...s, continueError: null, customAreaFromAd: null }));
  }, []);

  const showMap =
    mapState.propertyFetch.status === 'success' && Boolean(selectedProperty?.ref) && !loadError;

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto min-w-full flex max-w-full flex-col flex-col gap-6 p-6">
            <Text variant="text-xl/leading-normal/medium">Välj område på kartan</Text>

            {selectedProperty?.designation ? (
              <Text variant="text-base/leading-normal/normal" className="capitalize text-charcoal-700">
                Fastighet: {selectedProperty.designation}
              </Text>
            ) : null}

            {loadError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700">
                {loadError}
              </Text>
            ) : null}

            {loading ? (
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Laddar karta…
              </Text>
            ) : null}

            {!loading && !loadError && selectedProperty?.ref ? (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button
                    type="button"
                    variant={mapState.mapMode === MAP_MODES.sections ? 'secondary' : 'outline'}
                    size="medium"
                    className="!justify-start text-left whitespace-normal"
                    disabled={mapState.removeMode}
                    onClick={() => handleModeChange(MAP_MODES.sections)}
                  >
                    Välj avdelningar från min-skog
                  </Button>
                  <Button
                    type="button"
                    variant={mapState.mapMode === MAP_MODES.fellings ? 'secondary' : 'outline'}
                    size="medium"
                    className="!justify-start text-left whitespace-normal"
                    disabled={mapState.removeMode}
                    onClick={() => handleModeChange(MAP_MODES.fellings)}
                  >
                    Välj en avverkningsanmälan
                  </Button>
                  <Button
                    type="button"
                    variant={mapState.mapMode === MAP_MODES.draw ? 'secondary' : 'outline'}
                    size="medium"
                    className="!justify-start text-left whitespace-normal"
                    disabled={mapState.removeMode}
                    onClick={() => handleModeChange(MAP_MODES.draw)}
                  >
                    Rita område
                  </Button>
                </div>

                <div className="flex flex-col gap-2">
                  <Text variant="text-sm/leading-normal/medium" className="text-charcoal-700">
                    Placera markör
                  </Text>
                  <div className="flex flex-wrap gap-2">
                    {sellTimberTimberMarkerIconEntries().map(([key, opt]) => {
                      const active =
                        mapState.mapMode === MAP_MODES.marker &&
                        mapState.selectedMarkerIcon === key;
                      return (
                        <Button
                          key={key}
                          type="button"
                          variant={active ? 'secondary' : 'outline'}
                          size="medium"
                          disabled={mapState.removeMode}
                          onClick={() => selectMarkerModeWithIcon(key)}
                          className="!inline-flex !min-h-0 !items-center !justify-start gap-2 !py-2 !pr-3"
                          title={opt.label}
                        >
                          <img
                            src={`${Window.static_path}${opt.path}`}
                            alt=""
                            className="h-7 w-7 shrink-0 object-contain"
                          />
                          <span className="whitespace-nowrap">{opt.defaultName}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {mapModeStatus ? (
                  <Text variant="text-sm/leading-normal/normal" className={mapModeStatus.className}>
                    {mapModeStatus.content}
                  </Text>
                ) : null}

                <div className="flex flex-wrap items-center gap-3">
                  {mapState.removeMode ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="medium"
                      onClick={() => setMapState((s) => ({ ...s, removeMode: false }))}
                    >
                      Klar
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="medium"
                      disabled={!hasMapSelection}
                      onClick={() =>
                        setMapState((s) => ({ ...s, removeMode: true, markerMoveTargetId: null }))
                      }
                    >
                      Ta bort från kartan
                    </Button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </TailwindWrapper>

      {showMap ? (
        <div
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 24px 24px'
          }}
        >
          <SellTimberMap
            selection={{
              mapMode: mapState.mapMode,
              sections: mapState.sections,
              borderCoords: mapState.borderCoords,
              fellingApplicationsGeojson: mapState.fellingApplicationsGeojson,
              selectedSectionLocalIds: mapState.selectedSectionLocalIds,
              selectedFellingApplicationIds: mapState.selectedFellingApplicationIds,
              drawnPolygons: mapState.drawnPolygons,
              placedMarkers: mapState.placedMarkers,
              markerMoveTargetId: mapState.markerMoveTargetId
            }}
            handlers={{
              onToggleSection: toggleSection,
              onToggleFellingApplication: toggleFellingApplication,
              onDrawComplete,
              onMarkerAddAt,
              onMarkerSelectForMove,
              onMarkerMoveTo,
              onRemoveDrawnPolygon,
              onRemoveMarker,
              onRemoveHydratedAd
            }}
            options={{
              removeMode: mapState.removeMode,
              hydratedAdGeometry: mapState.customAreaFromAd,
              showAreaIdLabels: true
            }}
          />
        </div>
      ) : null}

      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto flex-col gap-3 px-6 pb-6">
          {mapState.continueError ? (
            <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
              {mapState.continueError}
            </Text>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="tertiary" onClick={() => dispatch({ type: 'SET_STEP', step: 1 })}>
              Tillbaka
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={mapState.removeMode}
              onClick={() => {
                const payload = {
                  mapMode: mapState.mapMode,
                  sections: mapState.sections,
                  borderCoords: mapState.borderCoords,
                  selectedSectionLocalIds: mapState.selectedSectionLocalIds,
                  selectedFellingApplicationIds: mapState.selectedFellingApplicationIds,
                  fellingApplicationsGeojson: mapState.fellingApplicationsGeojson,
                  drawnPolygons: mapState.drawnPolygons,
                  placedMarkers: mapState.placedMarkers,
                  customAreaFromAd: mapState.customAreaFromAd,
                  selectedMarkerIcon: mapState.selectedMarkerIcon
                };
                if (!hasMapGeometrySelection(payload)) {
                  setMapState((s) => ({
                    ...s,
                    continueError:
                      'Välj eller markera något på kartan innan du går vidare'
                  }));
                  return;
                }
                setMapState((s) => ({ ...s, continueError: null }));
                dispatch({ type: 'SET_GEOMETRY', payload });
                dispatch({ type: 'SET_STEP', step: 3 });
              }}
            >
              Gå vidare
            </Button>
          </div>
        </div>
      </TailwindWrapper>
    </>
  );
}
